import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ColumnComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { State, toODataString } from "@progress/kendo-data-query";
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from '../../../environments/environment';
import { FormFieldProcessingPhaseEnum } from '../enums/form-field-processing-phase.enum';
import { GridFlavorEnum } from '../enums/grid.enums';
import { IGridRow } from '../interfaces/grid-row.interface';
import { IFieldDefinitionLogic } from '../interfaces/ifield-definition-logic.interface';
import { FieldDefinition } from '../models/form-builder/field-definition.model';
import { FormField } from '../models/form-builder/form-field.model';
import { FormInstanceElement } from '../models/form-builder/form-instance-element.model';
import { GridRowViewModel } from '../models/form-builder/grid-row.model';
import { GridConfig } from '../models/grid/grid-config.model';
import { GridAllModesDataSource } from '../models/grid/grid-data-source.model';
import { GridFormInstanceElementWrapper } from '../models/grid/grid-form-instance-element-wrapper.model';
import { IGetCellDisplayValue } from '../models/grid/grid-interfaces';
import { GridRowDef } from '../models/grid/grid-row.model';
import { GridParams } from '../models/grid/grid-params.model';
import { FieldDefinitionService } from './field-definition.service';
import { FlexibleSelectionFieldService } from './flexible-selection-field.service';
import { ContactInfo } from '../models/contacts/contacts-form-field.model';
import { FieldIdToSelectedValues } from '../models/flexible-selection-fields/flexible-selection-field-instructions-request.model';
import { ValueConcatenatorService } from './value-concatenator.service';
import { FieldValue } from '../components/form-fields/value-concatenator-form-field/extras';
import { KendoGridDataResult } from '../interfaces/kendo-grid-data-result.interface';

export const KENDO_DATA_GRID_ROW_KEY: string = '__gridRow';
export const PARENT_GRID_FIELD_ID_KEY: string = '__parentGridFieldId';
export const GRID_COLUMN_ID_KEY: string = 'gridColumnId';

/*
    ============================================ IMPORTANT NOTE: ============================================ 
    This service propagates changes made in the UI to a user's _pending_ grid data on the server
    It does _not_ make changes to the saved, "live" data -- that is done when a FormInstance is saved
    =========================================================================================================

    This class extends RXJS's BehaviorSubject with a type arg of KendoGridDataResult.
    KendoGridRemoteBindingDirective subscribes to this, and so any changes to the this.dataResult property
    are picked up there and propagated to the grid view.
    When the this.dataResult properrty is updated, the subject is updated with calls to super.next(this.dataResult)
    so that subscribers get its latest value.
*/

@Injectable({
    providedIn: 'root'
})
export class KendoGridService extends BehaviorSubject<KendoGridDataResult> {
    // Properties.
    private url = environment.apiUrl;
    private endpoint: string = 'api/kendoGrid';
    private dataResult: KendoGridDataResult;
    private filtersAreSet: boolean;

    private loading = false;
    private reading: boolean = false;

    public get Loading() {
        return this.loading;
    }

    public constructor(
        private http: HttpClient,
        private fieldDefinitionService: FieldDefinitionService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private fieldValueConcatenatorService: ValueConcatenatorService) {
        super(<KendoGridDataResult>{ data: [], total: 0, flagGridRowsValueAsChanged: false, columnTotals: [] }) // init the base class's observed property
    }

    public read(gridConfig: GridConfig, fieldDefinitionService: FieldDefinitionService, getCellDisplayValue: IGetCellDisplayValue, formInstanceElementId: number, gridState: State): void {
        if (!this.reading) { // 10-08-2026 note:  oftentimes we were send out two read/get requests to the server simultaneously.
            this.reading = true;

            if (this.filteringHasStarted(gridState)) {
                this.filtersAreSet = true;
            } else if (this.filtersHaveBeenCleared(gridState)) {
                this.dataResult.columnTotals = null;
                this.filtersAreSet = false;
            }

            this.loading = true;
            this.fetch("Get", gridConfig, fieldDefinitionService, getCellDisplayValue, formInstanceElementId, gridState)
                .pipe(
                    tap((data) => {
                        this.dataResult = data;
                        this.loading = false;
                        this.reading = false;
                    })
                )
                .subscribe((data) => {
                    super.next(data); // TODO: to simplify the code, couldn't there be a call to super.next(this.dataResult) inside the above .pipe() call?
                });
        }
    }

    // PJH - added for VNEXT-1371 to allow the "Import Excel Data into Grid" dialog to get the pending edits it just created
    public readPending(gridConfig: GridConfig, fieldDefinitionService: FieldDefinitionService, getCellDisplayValue: IGetCellDisplayValue, formInstanceElementId: number, gridState: State): void {
        this.loading = true;
        this.dataResult.columnTotals = null; // causes fresh totals to always be calculated
        this.fetch("GetPending", gridConfig, fieldDefinitionService, getCellDisplayValue, formInstanceElementId, gridState)
            .pipe(
                tap((data) => {
                    this.dataResult = data;
                    this.loading = false;
                })
            )
            .subscribe((data) => {
                super.next(data); // TODO: to simplify the code, couldn't there be a call to super.next(this.dataResult) inside the above .pipe() call?
            });
    }

    public clearPending(formInstanceElementId: number) {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/clearPendingGridRows/${formInstanceElementId}`;

        return this.http.delete(url).toPromise().then(x => { });
    }

    public clearGridData(formInstanceElementId: number) {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/clearGridData/${formInstanceElementId}`;

        return this.http.delete(url).toPromise().then(x => { });
    }

    public addContactAsGridRow(formInstanceElementId: number,
        formInstanceId: number,
        formFieldId: number,
        user: ContactInfo) {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/createContactAsGridRow/${formInstanceElementId}`;

        return this.http.post(url, user).toPromise().then(x => { });
    }

    // TODO: Use a param object rather than all these params...
    // Returns true if successful, false if not
    public addOrUpdateRow(
        formInstanceElementId: number,
        formInstanceId: number,
        formFieldId: number,
        selectedGridRow: GridRowDef,
        valuesToFieldIds: FieldIdToSelectedValues,
        rowIndex: number,
        cols: any,
        dataItem: any,
        isNew: boolean,
        gridConfig: GridConfig,
        getCellDisplayValue: IGetCellDisplayValue,
        currentRowCount: number = 0): Promise<boolean> {
        // PJH - 3/31/2025 - added for VNEXT-1594 to support the new field value concatenator field
        // The Value Concatenator Field only supports including values from a single row Grid so ignore any rows that go beyond that
        if (rowIndex == 0) {
            this.propagateRowValuesForFieldConcatenation(selectedGridRow);
        }
        let gridRowViewModel = this.BuildGridRowViewModel(formInstanceId, formInstanceElementId, formFieldId, selectedGridRow, rowIndex, cols, gridConfig.ColumnDefs, currentRowCount);
        return this.flexibleSelectionFieldService.validateGridRow(gridRowViewModel, valuesToFieldIds).then(validationFailures => {
            if (validationFailures.length > 0) {
                let msg = validationFailures.map(x => x.validationFailureMessage).join(", ");
                alert(msg);
                return false;
            } else {
                if (isNew || selectedGridRow.DatabaseId == 0) {
                    return this.addRow(gridRowViewModel, gridConfig, getCellDisplayValue).then(x => {
                        return true;
                    });
                } else {
                    if (dataItem) {
                        return this.updateRow(gridRowViewModel, selectedGridRow, dataItem).then(x => {
                            return true;
                        });
                    }
                    else {
                        console.log('dataItem was null');
                        return false;
                    }
                }
            }
        });
    }

    private filteringHasStarted(gridState: State): boolean {
        return !this.filtersAreSet && gridState.filter?.filters.length > 0;
    }

    private filtersHaveBeenCleared(gridState: State) {
        return this.filtersAreSet == true && gridState.filter?.filters.length == 0;
    }

    // Added for VNEXT-1594
    // Prepares FieldValue objects and passes them to fieldValueConcatenatorService.updateEditedGridRow()
    // which makes updates available to any Value Concatenator fields on a Form
    private propagateRowValuesForFieldConcatenation(selectedGridRow: GridRowDef) {
        let fieldValues = [];
        for (const key in selectedGridRow.HshFormInstanceElementsById) {
            if (selectedGridRow.HshFormInstanceElementsById.hasOwnProperty(key)) {
                const el = selectedGridRow.HshFormInstanceElementsById[key];
                const iFieldLogic = this.fieldDefinitionService.getFieldClientLogicHandler(el.fieldClass);
                const val = iFieldLogic.getDisplayValue(null, el.formInstanceElement, null, null);
                let fieldValue = new FieldValue(el.formInstanceElement.formFieldId, el.fieldName, el.fieldDisplayName, val);
                fieldValues.push(fieldValue);
            }
        }
        this.fieldValueConcatenatorService.updateEditedGridRow(fieldValues);
    }

    /**
     * Submits a server request to remove a row from a grid.
     * @param gridRowDatabaseId - The id for the target row to be removed
     * @param gridFlavor - The type of grid being used
     */
    public removeRow(gridRowDatabaseId: number, gridFlavor: GridFlavorEnum): Promise<number> {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/${gridRowDatabaseId}/${gridFlavor}`;

        const findIndex = (dataItem) => { return dataItem.__gridRow.databaseId === gridRowDatabaseId };
        let indexToDelete = this.dataResult.data.findIndex(findIndex);

        return this.http.delete(url).toPromise().then(x => {
            this.updateColumnTotals(x, 'subtract');

            this.dataResult.data.splice(indexToDelete, 1); // remove from the UI

            return this.dataResult.data.length;
        });
    }

    public addRow(gridRow: GridRowViewModel, gridConfig: GridConfig, getCellDisplayValue: IGetCellDisplayValue) {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}`;
        if (!this.dataResult) this.reset();

        return this.http.post(url, gridRow).toPromise().then(x => {
            let wrappedGridRow: object = this.getWrappedGridRow(x, gridConfig, this.fieldDefinitionService, getCellDisplayValue, [x])
            this.dataResult.data.push(wrappedGridRow);

            this.updateColumnTotals(x);

            super.next(this.dataResult);
        });

    }

    private updateColumnTotals(x: Object, op: string = 'add') {
        const row = (x as GridRowViewModel);
        let colIndex = 0;
        // VNEXT-1505 - replace with Id - hmm, what will happen here if we're sending back name and id...
        for (const key in row.cellDataHash) {
            const cell = row.cellDataHash[key];
            if (cell.valueType == 'decimal') {
                if (this.dataResult.columnTotals == null) {
                    this.dataResult.columnTotals = {};
                }
                if (!this.dataResult.columnTotals[colIndex]) {
                    this.dataResult.columnTotals[colIndex] = 0;
                }
                if (op == 'add') {
                    this.dataResult.columnTotals[colIndex] += cell.decimalValue;
                }
                else {
                    this.dataResult.columnTotals[colIndex] -= cell.decimalValue;
                }
            }
            colIndex++;
        }
    }

    public updateRow(gridRow: GridRowViewModel, selectedGridRow: GridRowDef, dataItem: any) {
        if (dataItem) {
            let row = dataItem['__gridRow'];

            if (row) {
                let id = dataItem['__gridRow'].databaseId;
                gridRow.id = id;
                let col = 0;

                // IMPORTANT! update the dataItem which is the object Kendo Grid is using to display the row
                for (let prop in gridRow.cellDataHash) {
                    let originalValue: number = this.IsDecimal(gridRow, prop) ? this.prepDecimalValue(dataItem[prop]) : 0;
                    const value = this.getElementValue(
                        gridRow.cellDataHash[prop] as FormInstanceElement,
                        selectedGridRow.GridConfig.getFormField(parseInt(prop)),
                        row,
                        selectedGridRow.GridConfig.ColumnDefs
                    );
                    dataItem[prop] = value;

                    if (this.IsDecimal(gridRow, prop)) {
                        let updatedValue: number = this.prepDecimalValue(dataItem[prop]);
                        if (this.dataResult.columnTotals == null) {
                            this.dataResult.columnTotals = {};
                        }

                        this.dataResult.columnTotals[col] += (updatedValue - originalValue);
                    }

                    col++;
                }

                let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
                let url = `${baseUrl}${this.endpoint}/${gridRow.id}`;
                return this.http.put(url, gridRow).toPromise().then(x => { });
            }
        }
        else {
            console.log("dataItem is null for updateRow in the kendo grid service");
        }
    }

    private prepDecimalValue(value: string): number {
        if (value)
            try {
                const preppedValue = parseFloat(value.replace(/[$,]/g, ""));
                if (Number.isNaN(preppedValue)) {
                    alert(`Could not parse ${value}`);
                }
            } catch (e) {
                alert(e.message);
            }
        else
            return 0;
    }

    private IsDecimal(gridRow: GridRowViewModel, prop: string) {
        return gridRow.cellDataHash[prop].valueType == 'decimal';
    }

    public repositionRow(gridFlavor: GridFlavorEnum, formInstanceElementId: number, originalIndexOfMovedRow: number, movedRowId: number, originalIndexOfTargetRow: number, targetRowId: number, position: string): Promise<any> {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/${formInstanceElementId}/reorderRows`;
        let payload = {
            originalIndexOfMovedRow: originalIndexOfMovedRow,
            movedRowId: movedRowId,
            originalIndexOfTargetRow: originalIndexOfTargetRow,
            targetRowId: targetRowId,
            position: position,
            gridFlavor: gridFlavor
        }

        return this.http.post(url, payload).toPromise().then(x => {
            // now update the data for the UI
            let uiIndexOfMovedRow = this.dataResult.data.findIndex((dataItem) => { return dataItem.__gridRow.databaseId === movedRowId });
            let uiIndexOfTargetRow = this.dataResult.data.findIndex((dataItem) => { return dataItem.__gridRow.databaseId === targetRowId });

            let newIndex = uiIndexOfTargetRow;
            let direction = uiIndexOfMovedRow > uiIndexOfTargetRow ? "up" : "down";
            if (direction == "down" && position == "before") newIndex--;
            else if (direction == "up" && position == "after") newIndex++;

            this.reorderRowsInKendoGridData(uiIndexOfMovedRow, newIndex);

            super.next(this.dataResult);
        });
    }

    public lockRow(gridRowId: number, newLockStatus: boolean) {
        const baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        const url = `${baseUrl}${this.endpoint}/lockRow/${gridRowId}`;
        const payload = { isLocked: newLockStatus };

        this.http.put<void>(url, payload).subscribe({
            error: (err) => console.error('Error occurred', err)
        });
    }

    private reorderRowsInKendoGridData(uiIndexOfMovedRow: number, newIndex: number) {
        let movedRow = this.dataResult.data.splice(uiIndexOfMovedRow, 1)[0]; // first remove the moved Row
        let before = this.dataResult.data.slice(0, newIndex); // grab everything before its new location in the array
        let after = this.dataResult.data.slice(newIndex); // grab everything after its new location in the array

        this.dataResult.data = [
            ...before,
            movedRow,
            ...after
        ];
    }

    public resetItem(dataItem: object): void {
        if (!dataItem) {
            return;
        }

        // find orignal data item
        const originalDataItem = this.dataResult.data.find(
            (item) => item['id'] === dataItem['id']
        );

        // revert changes
        Object.assign(originalDataItem, dataItem);

        super.next(this.dataResult);
    }

    private getElementValue(el: FormInstanceElement, formField, gridRow: IGridRow, gridRowColumnDefs: FormField[]): any {
        let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
        return fieldDefClientLogic.getDisplayValue(formField, el, gridRow, null, gridRowColumnDefs);
    }

    private BuildGridRowViewModel(
        formInstanceId: number,
        formInstanceElementId: number,
        formFieldId: number,
        selectedGridRow: GridRowDef,
        rowIndex: number,
        cols: ColumnComponent[],
        colDefs: FormField[],
        currentRowCount: number): GridRowViewModel {
        let gridRowViewModel = new GridRowViewModel();
        gridRowViewModel.formInstanceElementId = formInstanceElementId;
        gridRowViewModel.formInstanceId = formInstanceId;
        gridRowViewModel.formFieldId = formFieldId;
        gridRowViewModel.id = selectedGridRow.DatabaseId;
        gridRowViewModel.rowIndex = currentRowCount > 0 ? currentRowCount : rowIndex; // for VNEXT-1371, new  rows need to set their rowIndex based on count of rows already in the grid
        gridRowViewModel.cellDataHash = {};
        gridRowViewModel.gridFlavor = selectedGridRow.GridFlavor;
        gridRowViewModel.isLocked = selectedGridRow.IsLocked;

        let columnHeaders = [];
        for (const col of cols) {
            if (col.field)
                columnHeaders.push(col.field);
        }
        let wrappers: GridFormInstanceElementWrapper[] = selectedGridRow.FormInstanceElementWrappers;
        for (let i = 0; i < wrappers.length; i++) {
            const wrapper = wrappers[i];
            const colDef = colDefs[i];
            const el = wrappers[i].formInstanceElement;
            // VNEXT-1505 - replaced name key with Id
            gridRowViewModel.cellDataHash[`${colDef.id}`] = el;
        }
        return gridRowViewModel;
    }

    private reset() {
        this.dataResult = <KendoGridDataResult>{ data: [], total: 0, flagGridRowsValueAsChanged: false };
    }

    public get DataResult(): KendoGridDataResult {
        return this.dataResult;
    }

    private fetch(
        method: string, // Get or GetPending -- this is the api endpoint method
        gridConfig: GridConfig,
        fieldDefinitionService: FieldDefinitionService,
        getCellDisplayValue: IGetCellDisplayValue,
        formInstanceElementId: number,
        gridState: State,
        action = "",
        data?: object[]): Observable<KendoGridDataResult> {
        let qs = toODataString(gridState);
        qs = qs.replace(/\$/g, '');
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/${method}/${formInstanceElementId}`;
        let paramObj = new GridParams(gridState, gridConfig.Flavor, gridConfig.FlavorModifier, this.dataResult?.columnTotals); // always send column totals even though it may not be used

        return <Observable<KendoGridDataResult>><unknown>this.http.post<KendoGridDataResult>(url, paramObj)
            .pipe(
                map((res) => {
                    let wrappedGridRows: object[] = [];
                    if ((res != null) && (res.data.length > 0)) {
                        for (let index: number = 0; index < res.data.length; index++) {
                            let untypedViewModel: object = res.data[index];
                            let wrappedGridRow: object = this.getWrappedGridRow(untypedViewModel, gridConfig, fieldDefinitionService, getCellDisplayValue, res.data);
                            wrappedGridRows.push(wrappedGridRow);
                        }
                    }
                    return <KendoGridDataResult>{
                        data: wrappedGridRows,
                        total: res.total,
                        flagGridRowsValueAsChanged: res.flagGridRowsValueAsChanged,
                        columnTotals: res.columnTotals
                    }
                })
            );
    }

    private getWrappedGridRow(untypedViewModel: object, gridConfig: GridConfig, fieldDefinitionService: FieldDefinitionService, getCellDisplayValue: IGetCellDisplayValue, res: object[]) {
        let typedViewModel: GridRowViewModel = plainToClass(GridRowViewModel, untypedViewModel);
        let gridRow: GridRowDef = this.unpackGridRowFromViewModel(gridConfig, fieldDefinitionService, getCellDisplayValue, typedViewModel, res.length);
        let wrappedGridRow: object = this.wrapGridRowAsObject(gridConfig, gridRow, fieldDefinitionService);
        return wrappedGridRow;
    }

    private serializeModels(data?: object[]): string {
        return data ? `&models=${JSON.stringify([data])}` : "";
    }

    private unpackGridRowFromViewModel(gridConfig: GridConfig, fieldDefinitionService: FieldDefinitionService, getCellDisplayValue: IGetCellDisplayValue, gridRowVM: GridRowViewModel, totalGridRows: number): GridRowDef {
        let gridRow: GridRowDef = GridAllModesDataSource.createGridRowFromGridViewModelObect(gridConfig, fieldDefinitionService, getCellDisplayValue, gridRowVM, 0, totalGridRows);

        return gridRow;
    }

    // The point of wrapping the grid row as a plain old object is that this is what Kendo needs. For each row it just wants a plain old JS object
    // with properties and values. 
    private wrapGridRowAsObject(gridConfig: GridConfig, gridRow: GridRowDef, fieldDefinitionService: FieldDefinitionService): object {
        let resultObject: object = { isEdited: false, isLocked: gridRow.IsLocked };
        resultObject[KENDO_DATA_GRID_ROW_KEY] = gridRow; // Here we also stuff in the GridRowDef itself. Kendo ignores it, but it can be useful to us for grabbing extra info
        resultObject[PARENT_GRID_FIELD_ID_KEY] = gridConfig.GridFormField.id;
        let colDefs: FormField[] = gridConfig.ColumnDefs;

        for (let index: number = 0; index < colDefs.length; index++) {
            let colDef: FormField = colDefs[index];

            let cellValue: string = ''; // Default for now.
            let formInstanceElement: FormInstanceElement = gridRow.getFormInstanceElement(colDef);
            if (formInstanceElement != null) {
                let fieldDefinition: FieldDefinition = fieldDefinitionService.getFieldDefinition(colDef.fieldDefinitionClassName);
                let fieldLogic: IFieldDefinitionLogic = fieldDefinition.customLogicHandler;
                cellValue = fieldLogic.getDisplayValue(colDef, formInstanceElement, gridRow, FormFieldProcessingPhaseEnum.LOADING_DATA, colDefs);
            }
            //resultObject[colDef.name] = cellValue; // VNEXT-1505-TODO: can drop this when done
            resultObject[colDef.id] = cellValue;
        }
        return resultObject;
    }
}
